import React from 'react'
import Head from 'next/head'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { coreResource, isBrowser } from 'env'
import breakpoint from 'tools/styled-components-breakpoint'
import Component from 'decorators/Component'
import { H1, LinkText, SmallText, H4 } from 'components/Typo'
import Flex from 'components/Flex'
import Button from 'components/Button'
import IconOddle from 'components/Icons/IconOddle'
import IconOddleEatsSquare from 'components/Icons/IconOddleEatsSquare'
import {
  margin,
  marginSizes,
  colors,
  buttonShapes,
  colorConstants,
  buttonStyles,
  padding,
  paddingSizes,
  borderRadius,
  borderRadiusSizes
} from 'theme/default'
import { parseLocale } from 'tools/localisation'
import Link from 'components/Link'
import IconArrow, { arrowDirections } from 'components/Icons/IconArrow'
import Collapsible from 'components/Collapsible'
import Accordion from 'components/Accordion'

const ErrorContainer = styled(Flex).attrs(() => ({
  justifyContent: 'center',
  alignItems: 'center'
}))`
  background-color: ${(props) =>
    props.theme.colorBackgroundColor || colors.background};
  text-align: center;
  height: 100vh;
  width: 100vw;
  /* stylelint-disable font-family-no-missing-generic-family-keyword */
  font-family: Roboto;
  /* stylelint-enable font-family-no-missing-generic-family-keyword */
`

const BackgroundImageContainer = styled(Flex).attrs(() => ({
  justifyContent: 'space-between'
}))`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 0;
`

const LeftImageContainer = styled(Flex).attrs(() => ({
  justifyContent: 'center',
  alignItems: 'start'
}))`
  margin-left: -20%;
  ${breakpoint('tablet')`
    margin: 0;
  `}
`

const RightImageContainer = styled(Flex).attrs(() => ({
  justifyContent: 'center',
  alignItems: 'flex-start'
}))`
  visibility: hidden;
  ${breakpoint('tablet')`
    visibility: visible;
  `}
`

const ImageContainer = styled(Flex).attrs(() => ({
  justifyContent: 'center'
}))`
  max-width: 80%;
`

const StyledContentContainer = styled(Flex).attrs(() => ({
  flexDirection: 'column',
  alignItems: 'center'
}))`
  max-width: 80%;
  ${breakpoint('tablet')`
    max-width: 60%;
  `}
  ${breakpoint('desktop')`
    max-width: 42%;
  `}
`

const StyledH4 = styled(H4).attrs(() => ({
  sectionHeading: true
}))`
  white-space: pre-wrap;
`

const ErrorHeaderContainer = styled(Flex)`
  cursor: pointer;
`

const ErrorDescription = styled(H4).attrs(() => ({
  sectionHeading: true
}))`
  white-space: pre-wrap;
  background-color: rgba(88, 88, 88, 0.1);
  color: #000000;
  padding: ${padding[paddingSizes.small]};
  border-radius: ${borderRadius[borderRadiusSizes.medium]};
`

const Icon = styled(IconArrow).attrs((props) => ({
  fill: props.fill,
  width: '10px',
  height: '10px'
}))`
  flex-shrink: 0;
  margin: ${margin[marginSizes.small]};
`

const StyledImage = styled.img`
  width: 100%;
  object-fit: contain;
`

const StyledSmallText = styled(SmallText).attrs((props) => ({
  color: props.theme.colorSectionHeadingsFont,
  normalised: true
}))``

const StyledSmallTextMultiLine = styled(SmallText).attrs((props) => ({
  color: props.theme.colorSectionHeadingsFont,
  normalised: true
}))``

const DefaultLinkComponent = styled(Link)`
  margin-top: ${margin[marginSizes.medium]};
`

const LinkedOddleEatsButton = Component()(({ t, LinkComponent, ctaLink }) => (
  <React.Fragment>
    <IconOddleEatsSquare alt='Oddle Eats' width='190px' height='60px' />
    <LinkComponent href={ctaLink || 'https://eats.oddle.me'} rel='nofollow'>
      <Button
        buttonStyle={buttonStyles.solid}
        buttonColor={colorConstants.oddleEats}
        buttonTextColor={colorConstants.white}
        buttonShape={buttonShapes.square}
      >
        {t('Take me to ALL the good food!')}
      </Button>
    </LinkComponent>
  </React.Fragment>
))

const Content = styled(Flex).attrs(() => ({
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center'
}))`
  width: 100%;
  height: 100%;
`

const ResetText = styled.span`
  text-decoration: underline;
  cursor: pointer;
`

export const errorTypes = {
  404: '404',
  500: '500',
  menuNotFound: 'menuNotFound',
  pageNotFound: 'pageNotFound',
  sectionNotFound: 'sectionNotFound',
  productNotFound: 'productNotFound',
  menuNotAvailable: 'menuNotAvailable',
  checkoutDirectAccess: 'checkoutDirectAccess',
  accountSuspended: 'accountSuspended',
  paymentPaid: 'paymentPaid',
  paymentLinkInvalid: 'paymentLinkInvalid',
  orderHasBeenConfirmed: 'orderHasBeenConfirmed',
  orderHasBeenCancelled: 'orderHasBeenCancelled',
  orderHasBeenFulfilled: 'orderHasBeenFulfilled',
  temporarilyUnavailable: 'temporarilyUnavailable',
  storeDeleted: 'storeDeleted',
  storeNotEnablePickupDelivery: 'storeNotEnablePickupDelivery',
  storeNoPaymentOptionPickup: 'storeNoPaymentOptionPickup',
  storeNoTimeOption: 'storeNoTimeOption',
  storeNotEnablePickup: 'storeNotEnablePickup',
  storeLocationNotFound: 'storeLocationNotFound',
  storeNoPaymentOptionDelivery: 'storeNoPaymentOptionDelivery',
  storeNotEnableDelivery: 'storeNotEnableDelivery',
  storeNotOpen: 'storeNotOpen',
  menuIsEmpty: 'menuIsEmpty'
}

const onReset = async (onClickReset) => {
  sendErrorReport('Error 500: Reset cookie button is clicked', {
    cookieSize: window.document.cookie.length
  })
  if (onClickReset) {
    await onClickReset()
  }
  clearAllCookies()
  window.location.reload()
}

const sendErrorReport = (text, meta) => {
  if (isBrowser() && window.Rollbar) {
    window.Rollbar.warning(text, meta)
  }
}

const clearAllCookies = () => {
  // https://stackoverflow.com/a/33366171
  // This is the extended script from above stackoverflow comment since it does not remove cookie from exact domain
  const cookies = window.document.cookie.split('; ')
  for (let c = 0; c < cookies.length; c++) {
    const d = [...window.location.hostname.split('.'), '']
    while (d.length > 0) {
      const cookieName = encodeURIComponent(
        cookies[c].split(';')[0].split('=')[0]
      )
      let cookieBase = `${cookieName}=; expires=Thu, 01-Jan-1970 00:00:01 GMT; `
      if (d.length > 0) {
        cookieBase += `domain=${d.filter((x) => x).join('.')}; `
      }
      cookieBase += 'path='
      const p = window.location.pathname.split('/')
      window.document.cookie = cookieBase + '/'
      while (p.length > 0) {
        window.document.cookie = cookieBase + p.join('/')
        p.pop()
      }
      d.shift()
    }
  }
}

const onLogout = () => {
  window.localStorage.clear()
  clearAllCookies()
  window.location.href = window.location.origin
}

class Error extends React.PureComponent {
  static propTypes = {
    locale: PropTypes.string,
    LinkComponent: PropTypes.element,
    type: PropTypes.oneOf(Object.keys(errorTypes)),
    packages: PropTypes.oneOf(['dashboard', 'fesc', 'feastbump']),
    ctaLink: PropTypes.string,
    onClickReset: PropTypes.func,
    errorDetails: PropTypes.object
  }

  static defaultProps = {
    LinkComponent: DefaultLinkComponent,
    locale: 'en_SG',
    type: '500',
    packages: 'fesc',
    source: 'FE'
  }

  // Related: https://oddle-pm.atlassian.net/wiki/spaces/TD/pages/523239425/Error+page+and+SEO+interaction
  getValue = () => {
    const {
      t,
      type,
      locale,
      LinkComponent,
      packages,
      ctaLink,
      onClickReset,
      errorDetails
    } = this.props
    const { country } = parseLocale(locale)

    switch (type) {
      case errorTypes['404']:
        return {
          title: t('Page Not Found'),
          text: (
            <StyledH4>
              {t(
                'The link you clicked may be broken or the page may have been removed.'
              )}
            </StyledH4>
          ),
          mainImageElement: (
            <StyledImage
              src={coreResource('/images/Error_404.png')}
              alt={t('Error 404')}
            />
          ),
          cta: (
            <LinkComponent
              onClick={(e) => {
                e.preventDefault()
                window.location.href = '/'
              }}
            >
              <Button>{t('Go to Homepage')}</Button>
            </LinkComponent>
          )
        }
      case errorTypes['500']:
        sendErrorReport('Error 500: Error page is displayed')
        return {
          title: t('Something went wrong!'),
          text: (
            <React.Fragment>
              <StyledH4>
                {t(
                  'We are working to bring this Shop back online as soon as we can!'
                )}
              </StyledH4>
              <StyledH4>
                {t(
                  'The restaurant and its partners are  working hard to prepare and fulfil the orders that have already been placed prior to the Shop being offline.'
                )}
              </StyledH4>
              {packages === 'fesc' && (
                <React.Fragment>
                  <StyledH4>
                    {t(
                      'You may try refreshing or _clickingHere_ to refresh the browser session.',
                      {
                        clickingHere: (
                          <ResetText
                            role='button'
                            onClick={() => onReset(onClickReset)}
                          >
                            {t('clicking here')}
                          </ResetText>
                        )
                      }
                    )}
                  </StyledH4>
                  <StyledSmallText>
                    <i>
                      {t(
                        'This action will log you out and clear your shopping cart.'
                      )}
                    </i>
                  </StyledSmallText>
                </React.Fragment>
              )}
              <StyledH4>
                {t(
                  "If the above doesn't work, please revisit this page in 15 minutes."
                )}
              </StyledH4>
            </React.Fragment>
          ),
          mainImageElement: (
            <StyledImage
              src={coreResource('/images/Error_500.png')}
              alt={t('Error 500')}
            />
          )
        }
      case errorTypes.temporarilyUnavailable:
        return {
          title: t('Something went wrong!'),
          text: (
            <React.Fragment>
              <StyledH4>
                {t(
                  "This Shop is temporarily unavailable and we're working to bring this Shop back online as soon as we can!"
                )}
              </StyledH4>
              <StyledSmallTextMultiLine>
                {t(
                  'In the meanwhile, you may\n1. Refresh this page (it works for some of the visitors).\n2. Visit this page in 15 minutes.'
                )}
              </StyledSmallTextMultiLine>
            </React.Fragment>
          ),
          mainImageElement: (
            <StyledImage
              src={coreResource('/images/Error_500.png')}
              alt={t('Error 500')}
            />
          )
        }
      case errorTypes.menuNotFound:
        return {
          title: t('Domain Not Found'),
          text: (
            <React.Fragment>
              <StyledH4>
                {t('This domain is currently unclaimed on Oddle.')}
              </StyledH4>
              <StyledH4 normalised>
                {t(
                  'If you are a restaurant owner and would like to claim this domain, please contact _link_.',
                  {
                    link: (
                      <LinkComponent href='mailto:support@oddle.me'>
                        <LinkText>support@oddle.me</LinkText>
                      </LinkComponent>
                    )
                  }
                )}
              </StyledH4>
              <StyledH4>
                {t(
                  'Alternatively, there are more than 500 restaurants at Oddle Eats.'
                )}
              </StyledH4>
            </React.Fragment>
          ),
          mainImageElement: <IconOddle width='100%' height='100%' />,
          cta: <LinkedOddleEatsButton LinkComponent={LinkComponent} />
        }
      case errorTypes.pageNotFound:
        return {
          title: t('Page Not Found'),
          text: (
            <StyledH4>
              {t(
                'The link you clicked may be broken or the page may have been removed.'
              )}
            </StyledH4>
          ),
          mainImageElement: <IconOddle width='100%' height='100%' alt='' />
        }
      case errorTypes.sectionNotFound:
        return {
          title: t('Oops..'),
          text: (
            <StyledH4>{t('We are having trouble loading this page.')}</StyledH4>
          ),
          mainImageElement: (
            <StyledImage
              src={coreResource('/images/Error_500.png')}
              alt={t('Error 500')}
            />
          )
        }
      case errorTypes.productNotFound:
        return {
          title: t('Product Not Found'),
          text: <StyledH4>{t('Product is no longer available.')}</StyledH4>,
          mainImageElement: (
            <StyledImage
              src={coreResource('/images/Error_404.png')}
              alt={t('Error 404')}
            />
          ),
          cta: (
            <LinkComponent href='/'>
              <Button>{t('Go to Homepage')}</Button>
            </LinkComponent>
          )
        }
      case errorTypes.menuNotAvailable:
        return {
          mainImageElement: <IconOddle width='100%' height='100%' />,
          title: t("We'll be back online soon!"),
          text: (
            <React.Fragment>
              <StyledH4>{t('Menu is unavailable.')}</StyledH4>
              <StyledH4>
                {t(
                  'Alternatively, there are more than 1,200 restaurants at Oddle Eats.'
                )}
              </StyledH4>
            </React.Fragment>
          ),
          cta:
            country === 'SG' ? (
              <LinkedOddleEatsButton
                LinkComponent={LinkComponent}
                ctaLink={ctaLink}
              />
            ) : null
        }
      case errorTypes.accountSuspended:
        return {
          mainImageElement: <IconOddle width='100%' height='100%' />,
          title: t("We'll be back soon!"),
          text: (
            <React.Fragment>
              <StyledH4>
                {t("If you're a visitor, please try again later.")}
              </StyledH4>
              <StyledH4>
                {t("If you're the owner of this store, please contact ")}
                <LinkComponent href='mailto:support@oddle.me'>
                  <LinkText>Oddle</LinkText>
                </LinkComponent>
              </StyledH4>
              {country === 'SG' ? (
                <StyledH4>
                  {t(
                    'Alternatively, there are more than 1,200 restaurants at Oddle Eats.'
                  )}
                </StyledH4>
              ) : null}
            </React.Fragment>
          ),
          cta:
            country === 'SG' ? (
              <LinkedOddleEatsButton LinkComponent={LinkComponent} />
            ) : null
        }
      case errorTypes.checkoutDirectAccess:
        sendErrorReport('Checkout direct access: Error page is displayed')
        return {
          title: t('Order is not ready for checkout.'),
          text: (
            <React.Fragment>
              <StyledH4>
                {t('Please return to the ordering page and checkout again.')}
              </StyledH4>
              <StyledH4>
                {t(
                  'If you are still facing this problem, _clickHere_ to refresh the browser session (this action will log you out and clear your shopping cart).',
                  {
                    clickHere: (
                      <ResetText role='button' onClick={onLogout}>
                        {t('click here')}
                      </ResetText>
                    )
                  }
                )}
              </StyledH4>
            </React.Fragment>
          ),
          cta: (
            <LinkComponent href='/'>
              <Button>{t('Take me back')}</Button>
            </LinkComponent>
          )
        }
      case errorTypes.paymentPaid:
        return {
          title: t('Payment has been made.'),
          text: (
            <StyledH4>
              {t('You are not required to make payment for this order.')}
            </StyledH4>
          ),
          mainImageElement: <IconOddle width='100%' height='100%' />,
          cta: (
            <LinkComponent href='/'>
              <Button>{t('Take me back')}</Button>
            </LinkComponent>
          )
        }
      case errorTypes.paymentLinkInvalid:
        return {
          title: t('Payment Link may have expired.'),
          text: (
            <StyledSmallText>
              {t(
                'You may\n1. Check that the payment link is correct, or\n2. Request for a new payment link from the restaurant.'
              )}
            </StyledSmallText>
          ),
          mainImageElement: <IconOddle width='100%' height='100%' />,
          cta: (
            <LinkComponent href='/'>
              <Button>{t('Take me back')}</Button>
            </LinkComponent>
          )
        }
      case errorTypes.orderHasBeenConfirmed:
        return {
          title: t('Oops...'),
          text: (
            <StyledH4>
              {t('This order has been processed by the restaurant partner')}
            </StyledH4>
          ),
          mainImageElement: <IconOddle width='100%' height='100%' />
        }
      case errorTypes.orderHasBeenFulfilled:
        return {
          title: t('Oops...'),
          text: (
            <StyledH4>
              {t('You should have received your order by now')}
            </StyledH4>
          ),
          mainImageElement: <IconOddle width='100%' height='100%' />
        }
      case errorTypes.orderHasBeenCancelled:
        return {
          title: t('Oops...'),
          text: <StyledH4>{t('This order has been cancelled')}</StyledH4>,
          mainImageElement: <IconOddle width='100%' height='100%' />
        }
      case errorTypes.storeDeleted:
        return {
          mainImageElement: <IconOddle width='100%' height='100%' />,
          title: t('Sorry, this store is currently closed.'),
          text: (
            <React.Fragment>
              <StyledH4>
                {t(
                  'No stores are available to accept delivery and takeaway orders.'
                )}
              </StyledH4>
              {country === 'SG' ? (
                <StyledH4>
                  {t(
                    'Alternatively, there are more than 1,200 restaurants at Oddle Eats.'
                  )}
                </StyledH4>
              ) : null}
            </React.Fragment>
          ),
          cta:
            country === 'SG' ? (
              <LinkedOddleEatsButton
                LinkComponent={LinkComponent}
                ctaLink={ctaLink}
              />
            ) : null
        }
      case errorTypes.storeNotEnablePickupDelivery:
        return {
          mainImageElement: <IconOddle width='100%' height='100%' />,
          title: t('Sorry, this store is currently closed.'),
          text: (
            <React.Fragment>
              <StyledH4>
                {t(
                  'No stores are available to accept delivery and takeaway orders.'
                )}
              </StyledH4>
              {country === 'SG' ? (
                <StyledH4>
                  {t(
                    'Alternatively, there are more than 1,200 restaurants at Oddle Eats.'
                  )}
                </StyledH4>
              ) : null}
            </React.Fragment>
          ),
          cta:
            country === 'SG' ? (
              <LinkedOddleEatsButton
                LinkComponent={LinkComponent}
                ctaLink={ctaLink}
              />
            ) : null
        }
      case errorTypes.storeNoPaymentOptionPickup:
        return {
          mainImageElement: <IconOddle width='100%' height='100%' />,
          title: t("We'll be back online soon!"),
          text: (
            <React.Fragment>
              <StyledH4>
                {t('Payments cannot be accepted for pickup orders.')}
              </StyledH4>
              {country === 'SG' ? (
                <StyledH4>
                  {t(
                    'Alternatively, there are more than 1,200 restaurants at Oddle Eats.'
                  )}
                </StyledH4>
              ) : null}
            </React.Fragment>
          ),
          cta:
            country === 'SG' ? (
              <LinkedOddleEatsButton
                LinkComponent={LinkComponent}
                ctaLink={ctaLink}
              />
            ) : null
        }
      case errorTypes.storeNoTimeOption:
        return {
          mainImageElement: <IconOddle width='100%' height='100%' />,
          title: t("We'll be back online soon!"),
          text: (
            <React.Fragment>
              <StyledH4>
                {t('There are no available delivery and pickup timeslots.')}
              </StyledH4>
              {country === 'SG' ? (
                <StyledH4>
                  {t(
                    'Alternatively, there are more than 1,200 restaurants at Oddle Eats.'
                  )}
                </StyledH4>
              ) : null}
            </React.Fragment>
          ),
          cta:
            country === 'SG' ? (
              <LinkedOddleEatsButton
                LinkComponent={LinkComponent}
                ctaLink={ctaLink}
              />
            ) : null
        }
      case errorTypes.storeNotEnablePickup:
        return {
          mainImageElement: <IconOddle width='100%' height='100%' />,
          title: t("We'll be back online soon!"),
          text: (
            <React.Fragment>
              <StyledH4>{t('Pickup orders are not accepted.')}</StyledH4>
              {country === 'SG' ? (
                <StyledH4>
                  {t(
                    'Alternatively, there are more than 1,200 restaurants at Oddle Eats.'
                  )}
                </StyledH4>
              ) : null}
            </React.Fragment>
          ),
          cta:
            country === 'SG' ? (
              <LinkedOddleEatsButton
                LinkComponent={LinkComponent}
                ctaLink={ctaLink}
              />
            ) : null
        }
      case errorTypes.storeLocationNotFound:
        return {
          mainImageElement: <IconOddle width='100%' height='100%' />,
          title: t("We'll be back online soon!"),
          text: (
            <React.Fragment>
              <StyledH4>{t('Delivery service is not available.')}</StyledH4>
              {country === 'SG' ? (
                <StyledH4>
                  {t(
                    'Alternatively, there are more than 1,200 restaurants at Oddle Eats.'
                  )}
                </StyledH4>
              ) : null}
            </React.Fragment>
          ),
          cta:
            country === 'SG' ? (
              <LinkedOddleEatsButton
                LinkComponent={LinkComponent}
                ctaLink={ctaLink}
              />
            ) : null
        }
      case errorTypes.storeNoPaymentOptionDelivery:
        return {
          mainImageElement: <IconOddle width='100%' height='100%' />,
          title: t("We'll be back online soon!"),
          text: (
            <React.Fragment>
              <StyledH4>
                {t('Payments cannot be accepted for delivery orders.')}
              </StyledH4>
              {country === 'SG' ? (
                <StyledH4>
                  {t(
                    'Alternatively, there are more than 1,200 restaurants at Oddle Eats.'
                  )}
                </StyledH4>
              ) : null}
            </React.Fragment>
          ),
          cta:
            country === 'SG' ? (
              <LinkedOddleEatsButton
                LinkComponent={LinkComponent}
                ctaLink={ctaLink}
              />
            ) : null
        }
      case errorTypes.storeNotEnableDelivery:
        return {
          mainImageElement: <IconOddle width='100%' height='100%' />,
          title: t("We'll be back online soon!"),
          text: (
            <React.Fragment>
              <StyledH4>{t('Delivery orders are not accepted.')}</StyledH4>
              {country === 'SG' ? (
                <StyledH4>
                  {t(
                    'Alternatively, there are more than 1,200 restaurants at Oddle Eats.'
                  )}
                </StyledH4>
              ) : null}
            </React.Fragment>
          ),
          cta:
            country === 'SG' ? (
              <LinkedOddleEatsButton
                LinkComponent={LinkComponent}
                ctaLink={ctaLink}
              />
            ) : null
        }
      case errorTypes.storeNotOpen:
        return {
          mainImageElement: <IconOddle width='100%' height='100%' />,
          title: t('Sorry, this Shop is currently closed.'),
          text:
            country === 'SG' ? (
              <StyledH4>
                {t(
                  'No stores are available to accept delivery and takeaway orders.'
                )}
              </StyledH4>
            ) : null,
          cta:
            country === 'SG' ? (
              <LinkedOddleEatsButton
                LinkComponent={LinkComponent}
                ctaLink={ctaLink}
              />
            ) : null
        }
      case errorTypes.menuIsEmpty:
        return {
          mainImageElement: <IconOddle width='100%' height='100%' />,
          title: t('Sorry, this store is currently closed.'),
          text: (
            <React.Fragment>
              <StyledH4>
                {t(
                  'No items are available for ordering'
                )}
              </StyledH4>
              {country === 'SG' ? (
                <StyledH4>
                  {t(
                    'Alternatively, there are more than 1,200 restaurants at Oddle Eats.'
                  )}
                </StyledH4>
              ) : null}
            </React.Fragment>
          ),
          cta:
            country === 'SG' ? (
              <LinkedOddleEatsButton
                LinkComponent={LinkComponent}
                ctaLink={ctaLink}
              />
            ) : null
        }
      default:
        return {
          mainImageElement: <IconOddle width='100%' height='100%' />,
          title: t('Something went wrong!'),
          text: (
            <React.Fragment>
              <Accordion>
                <Accordion.Section
                  id='undefinedError'
                  renderHeader={({ show }) => (
                    <ErrorHeaderContainer
                      width='100%'
                      flexDirection='row'
                      justifyContent='space-between'
                      alignItems='center'
                    >
                      <StyledH4 margin={marginSizes.extraSmall}>
                        {t(
                          'A clue for the Oddle team to resolve this issue faster'
                        )}
                      </StyledH4>
                      <Icon
                        fill={colors.heading}
                        direction={
                          show ? arrowDirections.up : arrowDirections.down
                        }
                      />
                    </ErrorHeaderContainer>
                  )}
                  renderContent={({ show }) => (
                    <Collapsible show={show}>
                      <ErrorDescription>
                        {errorDetails?.errorDescription
                          ? t(errorDetails.errorDescription)
                          : errorDetails.errorCode}
                      </ErrorDescription>
                    </Collapsible>
                  )}
                />
              </Accordion>
              <StyledH4>
                {t(
                  'Alternatively, there are more than 1,200 restaurants at Oddle Eats.'
                )}
              </StyledH4>
            </React.Fragment>
          ),
          cta:
            country === 'SG' ? (
              <LinkedOddleEatsButton
                LinkComponent={LinkComponent}
                ctaLink={ctaLink}
              />
            ) : null
        }
    }
  }

  render() {
    const { t } = this.props
    const { mainImageElement, title, text, cta, robots } = this.getValue()

    return (
      <ErrorContainer>
        <Head>
          <title>{title}</title>
          <link
            href={`//fonts.googleapis.com/css?family=Roboto`}
            rel='stylesheet'
          />
          {robots && <meta name='robots' content={robots} />}
        </Head>
        <Content>
          <BackgroundImageContainer>
            <LeftImageContainer>
              <StyledImage
                src={coreResource('/images/Background_Yoghurt.png')}
                alt={t('Background Yoghurt')}
              />
            </LeftImageContainer>
            <RightImageContainer>
              <StyledImage
                src={coreResource('/images/Background_Bread.png')}
                alt={t('Background Bread')}
              />
            </RightImageContainer>
          </BackgroundImageContainer>
          <StyledContentContainer>
            {mainImageElement && (
              <ImageContainer>{mainImageElement}</ImageContainer>
            )}
            {title && (
              <H1
                normalised
                sectionHeading
                marginTop={marginSizes.small}
                marginBottom={marginSizes.small}
              >
                {title}
              </H1>
            )}
            {text}
            {cta}
          </StyledContentContainer>
        </Content>
      </ErrorContainer>
    )
  }
}

export default Component()(Error)
